import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Activity, Brain, Cpu, ArrowUpRight, Play, Sparkles,
  Layers, Wallet, Link2, Box, Gauge, Server, ShieldCheck, Zap,
  ShoppingBag, MessageCircle, Star, Eye, Heart, Search, Headset
} from 'lucide-react';

/* ============================================================
   Shared layered background:
   - subtle Unsplash image texture (hybrid requirement)
   - color wash + grain + animated grid
   ============================================================ */
const LayeredBg = ({ image, children, tint = 'rgba(0,0,0,0.55)' }) => (
  <>
    <img
      src={image}
      alt=""
      className="absolute inset-0 w-full h-full object-cover opacity-40"
      draggable={false}
    />
    <div className="absolute inset-0" style={{ background: tint }} />
    <div
      className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
      style={{
        backgroundImage:
          'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.55%22/></svg>")',
      }}
    />
    <motion.div
      animate={{ opacity: [0.05, 0.12, 0.05] }}
      transition={{ duration: 5, repeat: Infinity }}
      className="absolute inset-0"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
        backgroundSize: '44px 44px',
        maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
      }}
    />
    {children}
  </>
);

/* ============================================================
   1. AI TRADING PLATFORM
   ============================================================ */
const AITradingVisual = ({ project }) => {
  const candles = [
    { h: 34, up: true, t: 18 }, { h: 46, up: false, t: 10 }, { h: 28, up: true, t: 26 },
    { h: 58, up: true, t: 4 }, { h: 40, up: false, t: 14 }, { h: 62, up: true, t: 2 },
    { h: 72, up: true, t: 0 }, { h: 50, up: false, t: 12 }, { h: 66, up: true, t: 6 },
    { h: 78, up: true, t: 2 }, { h: 54, up: false, t: 18 }, { h: 70, up: true, t: 4 },
  ];
  return (
    <LayeredBg
      image="https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1200&q=80"
      tint="linear-gradient(135deg, rgba(4,8,30,0.88), rgba(10,6,48,0.78))"
    >
      {/* Neural net nodes */}
      <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 600 380">
        {[[60,90],[60,190],[60,290],[220,140],[220,240],[380,110],[380,210],[380,310],[540,200]].map((p,i)=>(
          <motion.circle key={i} cx={p[0]} cy={p[1]} r="4" fill="#00D4FF"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2, delay: i*0.15, repeat: Infinity }} />
        ))}
        {[
          [60,90,220,140],[60,190,220,140],[60,190,220,240],[60,290,220,240],
          [220,140,380,110],[220,140,380,210],[220,240,380,210],[220,240,380,310],
          [380,110,540,200],[380,210,540,200],[380,310,540,200],
        ].map((l,i)=>(
          <motion.line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]}
            stroke="#00D4FF" strokeWidth="0.8"
            animate={{ opacity: [0.1, 0.5, 0.1] }}
            transition={{ duration: 3, delay: i*0.1, repeat: Infinity }} />
        ))}
      </svg>

      {/* Top ticker bar */}
      <motion.div
        initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="absolute top-5 left-5 right-5 flex items-center gap-3 bg-black/40 backdrop-blur-xl rounded-xl px-4 py-2.5 border border-white/10"
      >
        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
          className="w-2 h-2 rounded-full bg-[#00FF88]" />
        <span className="text-[10px] font-mono text-white/90 tracking-wider">LIVE • NYSE</span>
        <div className="flex items-center gap-3 ml-auto text-[11px] font-mono">
          {[
            { s:'AAPL', v:'+2.4%', up:true },
            { s:'NVDA', v:'+5.1%', up:true },
            { s:'TSLA', v:'-1.2%', up:false },
          ].map(t=>(
            <motion.span key={t.s} className={`flex items-center gap-1 ${t.up?'text-[#00FF88]':'text-[#FF6B6B]'}`}
              animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }}>
              <span className="text-white/70">{t.s}</span>{t.v}
            </motion.span>
          ))}
        </div>
      </motion.div>

      {/* Central candlestick chart card */}
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="absolute left-5 right-5 top-[58px] bottom-[78px] rounded-2xl bg-black/50 backdrop-blur-xl border border-white/10 p-4 overflow-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-mono text-[#00D4FF] uppercase tracking-widest">AI Prediction Engine</div>
            <div className="text-white text-xl font-bold font-space flex items-baseline gap-2">
              $284.52 <span className="text-[#00FF88] text-xs flex items-center gap-1"><ArrowUpRight className="w-3 h-3"/>+12.4%</span>
            </div>
          </div>
          <div className="flex gap-1">
            {['1D','1W','1M','1Y'].map((p,i)=>(
              <div key={p} className={`text-[9px] font-mono px-2 py-1 rounded-md ${i===2?'bg-[#00D4FF]/20 text-[#00D4FF] border border-[#00D4FF]/30':'text-white/40'}`}>{p}</div>
            ))}
          </div>
        </div>

        {/* Candles */}
        <div className="relative h-[calc(100%-52px)] flex items-end gap-[6px] pt-2">
          {/* Predicted line overlay */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
            <motion.path
              d="M 0 70 Q 15 55, 25 58 T 50 40 T 75 30 T 100 22"
              fill="none" stroke="#00D4FF" strokeWidth="0.6" strokeDasharray="2 2"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, delay: 0.5 }}/>
            <motion.circle cx="100" cy="22" r="1.5" fill="#00D4FF"
              animate={{ r: [1.5, 2.5, 1.5] }} transition={{ duration: 1.5, repeat: Infinity }}/>
          </svg>
          {candles.map((c,i)=>(
            <motion.div key={i}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: `${c.h}%`, opacity: 1 }}
              transition={{ delay: 0.3 + i*0.05, duration: 0.5, ease: 'easeOut' }}
              className="flex-1 flex flex-col items-center justify-end relative"
              style={{ minWidth: 8 }}>
              <div className="w-[2px] bg-white/30" style={{ height: `${c.t}%` }} />
              <div className={`w-full rounded-sm ${c.up?'bg-[#00FF88]':'bg-[#FF6B6B]'} shadow-[0_0_8px_currentColor]`}
                style={{ color: c.up?'#00FF88':'#FF6B6B', height: '70%' }} />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* AI confidence card */}
      <motion.div
        initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.6 }}
        className="absolute bottom-5 left-5 bg-black/60 backdrop-blur-xl rounded-xl border border-[#00D4FF]/30 px-3 py-2.5 flex items-center gap-3 shadow-xl"
      >
        <div className="w-8 h-8 rounded-lg bg-[#00D4FF]/15 flex items-center justify-center">
          <Brain className="w-4 h-4 text-[#00D4FF]" />
        </div>
        <div>
          <div className="text-[9px] text-white/50 font-mono uppercase">AI Confidence</div>
          <div className="text-white text-sm font-bold font-space">99.4%</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 }}
        className="absolute bottom-5 right-5 bg-black/60 backdrop-blur-xl rounded-xl border border-white/10 px-3 py-2.5 flex items-center gap-3 shadow-xl"
      >
        <Activity className="w-4 h-4 text-[#00FF88]" />
        <div>
          <div className="text-[9px] text-white/50 font-mono uppercase">Latency</div>
          <div className="text-white text-sm font-bold font-space">0.8ms</div>
        </div>
      </motion.div>
    </LayeredBg>
  );
};

/* ============================================================
   2. DEFI LENDING PROTOCOL  (hex-grid + token flow)
   ============================================================ */
const DeFiVisual = ({ project }) => {
  // hex coordinates
  const hexes = [];
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 8; c++) {
      hexes.push({ x: 40 + c * 70 + (r % 2) * 35, y: 50 + r * 60, i: r*8+c });
    }
  }
  return (
    <LayeredBg
      image="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&q=80"
      tint="linear-gradient(135deg, rgba(2,20,18,0.88), rgba(0,40,30,0.78))"
    >
      {/* Hex mesh */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 380" preserveAspectRatio="xMidYMid slice">
        <defs>
          <polygon id="hex" points="20,0 40,12 40,36 20,48 0,36 0,12" />
          <linearGradient id="hexGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00FF88" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.1"/>
          </linearGradient>
        </defs>
        {hexes.map(h=>(
          <motion.use key={h.i} href="#hex" x={h.x} y={h.y}
            fill="none" stroke="#00FF88" strokeOpacity="0.2" strokeWidth="0.6"
            animate={{ strokeOpacity: [0.1, 0.35, 0.1] }}
            transition={{ duration: 3, delay: (h.i%9)*0.12, repeat: Infinity }}/>
        ))}
        {/* highlight a few filled hexes */}
        {[{x:180,y:110},{x:320,y:170},{x:460,y:110},{x:250,y:230}].map((h,i)=>(
          <motion.use key={`f${i}`} href="#hex" x={h.x} y={h.y} fill="url(#hexGrad)" stroke="#00FF88" strokeOpacity="0.8"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2.5, delay: i*0.4, repeat: Infinity }}/>
        ))}
        {/* Flowing token lines */}
        <motion.path d="M 200 134 Q 260 80, 340 194 T 480 134"
          fill="none" stroke="#00D4FF" strokeWidth="1.2" strokeDasharray="4 6"
          animate={{ strokeDashoffset: [0, -40] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
      </svg>

      {/* TVL headline card */}
      <motion.div
        initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="absolute top-5 left-5 bg-black/50 backdrop-blur-xl rounded-xl border border-[#00FF88]/25 px-4 py-3 shadow-2xl"
      >
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-3.5 h-3.5 text-[#00FF88]" />
          <span className="text-[10px] font-mono text-[#00FF88] uppercase tracking-wider">Total Value Locked</span>
        </div>
        <div className="text-white text-2xl font-bold font-space">$25.4M</div>
        <div className="flex items-center gap-1 text-[10px] text-[#00FF88] font-mono">
          <TrendingUp className="w-3 h-3"/>+18.2% 24h
        </div>
      </motion.div>

      {/* Chain badges */}
      <motion.div
        initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
        className="absolute top-5 right-5 flex flex-col gap-2"
      >
        {[
          { n:'ETH', c:'#627EEA' },
          { n:'POLYGON', c:'#8247E5' },
          { n:'BSC', c:'#F3BA2F' },
        ].map((chain,i)=>(
          <motion.div key={chain.n}
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i*0.1 }}
            className="flex items-center gap-2 bg-black/55 backdrop-blur-xl rounded-lg border border-white/10 px-3 py-1.5"
          >
            <div className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]" style={{ background: chain.c, color: chain.c }}/>
            <span className="text-[10px] font-mono text-white/90">{chain.n}</span>
          </motion.div>
        ))}
      </motion.div>

      {/* Animated token pool cards */}
      <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-3">
        {[
          { sym:'ETH', apy:'12.4%', col:'#627EEA', icon:<Wallet className="w-3.5 h-3.5"/> },
          { sym:'USDC', apy:'8.2%', col:'#2775CA', icon:<Layers className="w-3.5 h-3.5"/> },
          { sym:'BTC', apy:'6.8%', col:'#F7931A', icon:<Link2 className="w-3.5 h-3.5"/> },
        ].map((p,i)=>(
          <motion.div key={p.sym}
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 + i*0.1 }}
            className="bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 p-3 relative overflow-hidden"
          >
            <motion.div
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3 + i*0.5, repeat: Infinity, ease: 'linear' }}
              className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/5 to-transparent"
            />
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${p.col}25`, color: p.col }}>
                {p.icon}
              </div>
              <span className="text-[11px] font-bold text-white font-space">{p.sym}</span>
            </div>
            <div className="text-[9px] text-white/50 uppercase font-mono">APY</div>
            <div className="text-[#00FF88] text-base font-bold font-space">{p.apy}</div>
          </motion.div>
        ))}
      </div>
    </LayeredBg>
  );
};

/* ============================================================
   3. METAVERSE VR (isometric world)
   ============================================================ */
const VRVisual = ({ project }) => {
  return (
    <LayeredBg
      image="https://images.unsplash.com/photo-1626387346567-68d0c4e2887f?w=1200&q=80"
      tint="linear-gradient(135deg, rgba(30,5,50,0.85), rgba(60,10,80,0.75))"
    >
      {/* Central energy glow */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 w-[60%] h-[60%] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(200,80,192,0.45) 0%, rgba(123,97,255,0.2) 40%, transparent 70%)' }}
      />

      {/* Isometric grid platform */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 380" preserveAspectRatio="xMidYMid slice">
        <defs>
          <radialGradient id="vrPlatform" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C850C0" stopOpacity="0.55"/>
            <stop offset="60%" stopColor="#7B61FF" stopOpacity="0.15"/>
            <stop offset="100%" stopColor="#C850C0" stopOpacity="0"/>
          </radialGradient>
        </defs>
        {/* Glowing platform disc */}
        <ellipse cx="300" cy="250" rx="240" ry="70" fill="url(#vrPlatform)" />

        {/* Ground grid — isometric (brighter) */}
        <g transform="translate(300,250)" opacity="0.85">
          {Array.from({length:13}).map((_,i)=>{
            const o = i-6;
            return (
              <g key={i}>
                <motion.line x1={-240+o*38} y1={-o*19} x2={o*38} y2={120-o*19}
                  stroke="#C850C0" strokeWidth="1" strokeOpacity="0.6"
                  animate={{ strokeOpacity: [0.3, 0.85, 0.3] }}
                  transition={{ duration: 3, delay: i*0.12, repeat: Infinity }} />
                <motion.line x1={-o*38} y1={120-o*19} x2={240-o*38} y2={-o*19}
                  stroke="#7B61FF" strokeWidth="1" strokeOpacity="0.55"
                  animate={{ strokeOpacity: [0.25, 0.75, 0.25] }}
                  transition={{ duration: 3.5, delay: i*0.1, repeat: Infinity }} />
              </g>
            );
          })}
        </g>

        {/* Central hero cube (large) */}
        <g transform="translate(300,170)">
          <motion.g
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            {/* glow */}
            <ellipse cx="0" cy="60" rx="60" ry="10" fill="#C850C0" opacity="0.4"/>
            {/* top */}
            <polygon points="0,-40 55,-13 0,14 -55,-13" fill="#C850C0" opacity="0.95"/>
            <polygon points="0,-40 55,-13 0,14 -55,-13" fill="none" stroke="#FF6B6B" strokeWidth="1.5" opacity="0.9"/>
            {/* right */}
            <polygon points="0,14 55,-13 55,40 0,67" fill="#7B61FF" opacity="0.85"/>
            <polygon points="0,14 55,-13 55,40 0,67" fill="none" stroke="#C850C0" strokeWidth="1" opacity="0.7"/>
            {/* left */}
            <polygon points="0,14 -55,-13 -55,40 0,67" fill="#4A3AFF" opacity="0.75"/>
            <polygon points="0,14 -55,-13 -55,40 0,67" fill="none" stroke="#7B61FF" strokeWidth="1" opacity="0.7"/>
            {/* inner core */}
            <motion.circle cx="0" cy="5" r="10" fill="#fff" opacity="0.5"
              animate={{ r: [8, 16, 8] }} transition={{ duration: 2, repeat: Infinity }}/>
          </motion.g>
        </g>

        {/* Floating isometric cubes around hero */}
        {[
          { x:120, y:180, s:1.2, c:'#FF6B6B', d:0 },
          { x:470, y:160, s:1.4, c:'#00D4FF', d:0.3 },
          { x:160, y:100, s:0.9, c:'#C850C0', d:0.6 },
          { x:440, y:260, s:1, c:'#FF8C42', d:0.9 },
          { x:520, y:100, s:0.8, c:'#7B61FF', d:1.2 },
        ].map((b,i)=>(
          <g key={i} transform={`translate(${b.x},${b.y})`}>
            <motion.g
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 4+i*0.3, delay: b.d, repeat: Infinity, ease: 'easeInOut' }}
            >
              <polygon points={`0,${-22*b.s} ${28*b.s},${-9*b.s} 0,${4*b.s} ${-28*b.s},${-9*b.s}`}
                fill={b.c} opacity="1"/>
              <polygon points={`0,${4*b.s} ${28*b.s},${-9*b.s} ${28*b.s},${22*b.s} 0,${35*b.s}`}
                fill={b.c} opacity="0.7"/>
              <polygon points={`0,${4*b.s} ${-28*b.s},${-9*b.s} ${-28*b.s},${22*b.s} 0,${35*b.s}`}
                fill={b.c} opacity="0.45"/>
              <polygon points={`0,${-22*b.s} ${28*b.s},${-9*b.s} 0,${4*b.s} ${-28*b.s},${-9*b.s}`}
                fill="none" stroke="#fff" strokeOpacity="0.4" strokeWidth="0.8"/>
            </motion.g>
          </g>
        ))}

        {/* Particle dots */}
        {Array.from({length:20}).map((_,i)=>{
          const x = 60 + (i*31)%520;
          const y = 30 + (i*47)%120;
          return (
            <motion.circle key={i} cx={x} cy={y} r="1.5" fill="#fff"
              animate={{ opacity: [0.2, 0.9, 0.2], cy: [y, y-8, y] }}
              transition={{ duration: 3+i*0.1, repeat: Infinity, delay: i*0.1 }}/>
          );
        })}
      </svg>

      {/* Top HUD */}
      <motion.div
        initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="absolute top-5 left-5 right-5 flex items-center justify-between"
      >
        <div className="bg-black/55 backdrop-blur-xl rounded-xl border border-[#C850C0]/30 px-4 py-2.5 flex items-center gap-3 shadow-2xl">
          <Headset className="w-4 h-4 text-[#C850C0]" />
          <div>
            <div className="text-[9px] font-mono text-[#C850C0] uppercase tracking-widest">Immersive Session</div>
            <div className="text-white text-sm font-bold font-space">Zone: Nexus Hub</div>
          </div>
        </div>
        <div className="bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 px-3 py-2 flex items-center gap-2">
          <motion.div animate={{ scale: [1,1.4,1] }} transition={{ duration: 1.2, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-[#00FF88]" />
          <span className="text-[10px] font-mono text-white/90">32 PLAYERS</span>
        </div>
      </motion.div>

      {/* Player avatars in bottom */}
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
        className="absolute bottom-5 left-5 right-5 flex items-center justify-between"
      >
        <div className="flex items-center -space-x-2">
          {[
            ['#FF6B6B','K'],['#00D4FF','J'],['#00FF88','M'],['#C850C0','A'],['#FF8C42','S']
          ].map(([c,l],i)=>(
            <motion.div key={i}
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.5 + i*0.08, type:'spring' }}
              whileHover={{ y: -4, zIndex: 5 }}
              className="w-9 h-9 rounded-full border-2 border-black/50 flex items-center justify-center text-xs font-bold text-white shadow-lg"
              style={{ background: c, boxShadow: `0 0 12px ${c}66` }}
            >
              {l}
            </motion.div>
          ))}
          <div className="w-9 h-9 rounded-full bg-white/10 backdrop-blur border-2 border-black/50 flex items-center justify-center text-[10px] font-mono text-white">+27</div>
        </div>

        <div className="bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 px-3 py-2 flex items-center gap-2">
          <Play className="w-3 h-3 text-[#00FF88]" fill="#00FF88"/>
          <span className="text-[10px] font-mono text-white/90">45 min session</span>
        </div>
      </motion.div>

      {/* Rating badge */}
      <motion.div
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.6, type: 'spring' }}
        className="absolute top-1/2 right-6 -translate-y-1/2 bg-black/55 backdrop-blur-xl rounded-2xl border border-[#C850C0]/30 p-3 shadow-2xl"
      >
        <div className="flex items-center gap-1 mb-1">
          {[1,2,3,4,5].map(i=>(
            <Star key={i} className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
          ))}
        </div>
        <div className="text-white text-lg font-bold font-space">4.8</div>
        <div className="text-[9px] text-white/60 font-mono">5,000+ reviews</div>
      </motion.div>
    </LayeredBg>
  );
};

/* ============================================================
   4. ENTERPRISE SAAS DASHBOARD
   ============================================================ */
const SaaSVisual = ({ project }) => {
  const bars = [45, 62, 38, 78, 55, 82, 68, 91, 72, 88];
  return (
    <LayeredBg
      image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80"
      tint="linear-gradient(135deg, rgba(2,10,28,0.88), rgba(0,30,60,0.78))"
    >
      {/* Side nav */}
      <motion.div
        initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
        className="absolute left-5 top-5 bottom-5 w-14 bg-black/55 backdrop-blur-xl rounded-2xl border border-white/10 flex flex-col items-center py-4 gap-3"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00D4FF] to-[#0077FF] flex items-center justify-center shadow-[0_0_12px_rgba(0,212,255,0.6)]">
          <Cpu className="w-4 h-4 text-white"/>
        </div>
        <div className="h-px w-6 bg-white/10" />
        {[Gauge, Layers, Server, Activity, ShieldCheck].map((Icon,i)=>(
          <motion.div key={i}
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(0,212,255,0.15)' }}
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${i===0?'bg-[#00D4FF]/15 text-[#00D4FF]':'text-white/40 hover:text-white'}`}>
            <Icon className="w-3.5 h-3.5"/>
          </motion.div>
        ))}
      </motion.div>

      {/* Top stats */}
      <div className="absolute top-5 left-[88px] right-5 grid grid-cols-3 gap-3">
        {[
          { l:'Uptime', v:'99.99%', c:'#00FF88', icon:<ShieldCheck className="w-3 h-3"/> },
          { l:'Requests', v:'12.4M', c:'#00D4FF', icon:<Activity className="w-3 h-3"/> },
          { l:'Latency', v:'42ms', c:'#7B61FF', icon:<Zap className="w-3 h-3"/> },
        ].map((s,i)=>(
          <motion.div key={s.l}
            initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 + i*0.08 }}
            className="bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 px-3 py-2.5 relative overflow-hidden"
          >
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: s.c }}>
              {s.icon}{s.l}
            </div>
            <div className="text-white text-lg font-bold font-space">{s.v}</div>
            <motion.div
              animate={{ x: ['-100%','200%'] }}
              transition={{ duration: 3, delay: i*0.4, repeat: Infinity, ease:'linear' }}
              className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none"/>
          </motion.div>
        ))}
      </div>

      {/* Main chart */}
      <motion.div
        initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.25 }}
        className="absolute left-[88px] right-5 top-[92px] bottom-[96px] bg-black/55 backdrop-blur-xl rounded-2xl border border-white/10 p-4 overflow-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-mono text-[#00D4FF] uppercase tracking-widest">Requests / minute</div>
            <div className="text-white text-xl font-bold font-space">1,284,902</div>
          </div>
          <div className="flex gap-1.5">
            <div className="flex items-center gap-1 text-[9px] text-white/60 font-mono"><div className="w-2 h-2 rounded-full bg-[#00D4FF]"/>API</div>
            <div className="flex items-center gap-1 text-[9px] text-white/60 font-mono"><div className="w-2 h-2 rounded-full bg-[#00FF88]"/>WS</div>
          </div>
        </div>

        {/* Bar + line chart combo */}
        <div className="relative h-[calc(100%-44px)] flex items-end gap-2 pt-2">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
            <motion.path d="M 0 80 L 10 70 L 20 75 L 30 55 L 40 62 L 50 40 L 60 48 L 70 28 L 80 35 L 90 18 L 100 12"
              fill="none" stroke="#00FF88" strokeWidth="0.8"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2 }}/>
          </svg>
          {bars.map((h,i)=>(
            <motion.div key={i}
              initial={{ height: 0 }} animate={{ height: `${h}%` }}
              transition={{ delay: 0.4 + i*0.05, duration: 0.6 }}
              className="flex-1 rounded-t-md bg-gradient-to-t from-[#00D4FF]/70 to-[#00D4FF]/20 border-t border-[#00D4FF] relative"
            >
              {i===7 && (
                <motion.div
                  animate={{ y: [-3, 3, -3] }} transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-6 left-1/2 -translate-x-1/2 bg-[#00D4FF] text-black text-[9px] font-bold font-mono px-1.5 py-0.5 rounded"
                >PEAK</motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Server racks */}
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
        className="absolute bottom-5 left-[88px] right-5 bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 px-4 py-3 flex items-center gap-4"
      >
        <Server className="w-4 h-4 text-[#00FF88]"/>
        <div className="flex-1 flex gap-1.5">
          {Array.from({length:24}).map((_,i)=>{
            const state = i%7===0 ? 'err' : i%3===0 ? 'warn' : 'ok';
            const col = state==='ok'?'#00FF88':state==='warn'?'#FFB800':'#FF6B6B';
            return (
              <motion.div key={i}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.5, delay: i*0.05, repeat: Infinity }}
                className="flex-1 h-6 rounded-sm"
                style={{ background: col, boxShadow: `0 0 6px ${col}80` }}
              />
            );
          })}
        </div>
        <div className="text-[10px] font-mono text-white/70">200+ nodes</div>
      </motion.div>
    </LayeredBg>
  );
};

/* ============================================================
   5. AI SHOPPING ASSISTANT (phone + AR + chat)
   ============================================================ */
const ShoppingVisual = ({ project }) => {
  return (
    <LayeredBg
      image="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80"
      tint="linear-gradient(135deg, rgba(40,10,5,0.85), rgba(60,5,40,0.78))"
    >
      {/* Background glow */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,140,66,0.35), transparent 70%)' }}
      />

      {/* Phone mockup */}
      <motion.div
        initial={{ y: 30, opacity: 0, rotateZ: -4 }}
        animate={{ y: 0, opacity: 1, rotateZ: -4 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="absolute left-[8%] top-6 bottom-6 w-[38%] rounded-[28px] bg-gradient-to-br from-[#2a1a2e] to-[#1a0a1e] border-4 border-black/60 shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 20px 60px rgba(255,140,66,0.25), inset 0 0 0 1px rgba(255,255,255,0.1)' }}
      >
        {/* notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-b-xl z-10" />
        {/* AR view */}
        <div className="h-3/5 relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80"
            alt="" className="absolute inset-0 w-full h-full object-cover opacity-85"/>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"/>
          {/* AR scan frame */}
          <motion.div
            animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }}
            className="absolute top-4 left-4 right-4 bottom-4 border-2 border-[#FF8C42] rounded-xl"
            style={{ clipPath: 'polygon(0 0,24px 0,24px 2px,2px 2px,2px 24px,0 24px,0 0,100% 0,100% 24px,calc(100% - 2px) 24px,calc(100% - 2px) 2px,calc(100% - 24px) 2px,calc(100% - 24px) 0,100% 0,100% 100%,calc(100% - 24px) 100%,calc(100% - 24px) calc(100% - 2px),calc(100% - 2px) calc(100% - 2px),calc(100% - 2px) calc(100% - 24px),100% calc(100% - 24px),100% 100%,0 100%,0 calc(100% - 24px),2px calc(100% - 24px),2px calc(100% - 2px),24px calc(100% - 2px),24px 100%,0 100%)' }}
          />
          {/* scanning line */}
          <motion.div
            animate={{ y: [0, 120, 0] }} transition={{ duration: 2.5, repeat: Infinity, ease:'easeInOut' }}
            className="absolute left-6 right-6 h-0.5 bg-[#FF8C42] shadow-[0_0_12px_#FF8C42]"
            style={{ top: '20px' }}
          />
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-1">
            <motion.div animate={{ scale: [1,1.3,1] }} transition={{ duration: 1, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-[#FF8C42]"/>
            <span className="text-[8px] font-mono text-white/90">AR MODE</span>
          </div>
        </div>
        {/* Product info */}
        <div className="p-3">
          <div className="text-[9px] font-mono text-[#FF8C42] uppercase tracking-widest mb-1">Scanned Item</div>
          <div className="text-white text-sm font-bold font-space leading-tight">Nordic Leather Chair</div>
          <div className="flex items-center justify-between mt-2">
            <div className="text-white text-base font-bold">$289</div>
            <motion.div whileHover={{ scale: 1.05 }}
              className="bg-[#FF8C42] text-black text-[10px] font-bold font-mono px-3 py-1.5 rounded-lg shadow-[0_0_10px_#FF8C42]"
            >BUY NOW</motion.div>
          </div>
        </div>
      </motion.div>

      {/* AI Chat bubble */}
      <motion.div
        initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}
        className="absolute right-5 top-8 w-[48%] space-y-2"
      >
        <div className="flex items-center gap-2 bg-black/55 backdrop-blur-xl rounded-xl border border-[#FF8C42]/30 px-3 py-2 shadow-xl">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF8C42] to-[#C850C0] flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white"/>
          </div>
          <div>
            <div className="text-[9px] font-mono text-[#FF8C42] uppercase">AI Stylist</div>
            <div className="text-white text-[11px]">"Here's a match for your living room style"</div>
          </div>
        </div>

        {/* Product recommendation cards */}
        {[
          { n:'Oak Side Table', p:'$149', m:'92% match' },
          { n:'Wool Area Rug', p:'$329', m:'88% match' },
        ].map((p,i)=>(
          <motion.div key={i}
            initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.7 + i*0.12 }}
            whileHover={{ x: -4 }}
            className="flex items-center gap-3 bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 p-2.5 shadow-xl"
          >
            <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden">
              <div className="w-full h-full bg-gradient-to-br from-[#FF8C42]/60 to-[#C850C0]/60"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[11px] font-bold truncate">{p.n}</div>
              <div className="flex items-center gap-2">
                <div className="text-[#FF8C42] text-[10px] font-mono">{p.p}</div>
                <div className="text-[9px] text-[#00FF88] font-mono flex items-center gap-0.5">
                  <Sparkles className="w-2.5 h-2.5"/>{p.m}
                </div>
              </div>
            </div>
            <Heart className="w-3.5 h-3.5 text-white/40"/>
          </motion.div>
        ))}

        {/* Conversion stat */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }}
          className="flex items-center gap-3 bg-gradient-to-r from-[#FF8C42]/20 to-[#C850C0]/20 backdrop-blur-xl rounded-xl border border-[#FF8C42]/30 px-3 py-2.5 shadow-xl"
        >
          <TrendingUp className="w-4 h-4 text-[#FF8C42]"/>
          <div>
            <div className="text-[9px] font-mono text-white/60 uppercase">Conversion Lift</div>
            <div className="text-white text-base font-bold font-space">+85%</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom floating cart */}
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }}
        className="absolute bottom-5 left-5 bg-black/55 backdrop-blur-xl rounded-xl border border-white/10 px-3 py-2 flex items-center gap-2 shadow-xl"
      >
        <ShoppingBag className="w-3.5 h-3.5 text-[#FF8C42]"/>
        <span className="text-[10px] font-mono text-white/90">50K orders / mo</span>
      </motion.div>
    </LayeredBg>
  );
};

/* ============================================================
   Dispatcher
   ============================================================ */
const VISUALS = {
  1: AITradingVisual,
  2: DeFiVisual,
  3: VRVisual,
  4: SaaSVisual,
  5: ShoppingVisual,
};

export default function CaseStudyVisual({ project }) {
  const Component = VISUALS[project.id] || AITradingVisual;
  return (
    <div
      data-testid={`case-study-visual-${project.id}`}
      className="relative w-full aspect-[16/10] rounded-2xl border border-[rgba(255,255,255,0.1)] overflow-hidden shadow-2xl bg-black"
    >
      <Component project={project} />
      {/* Hover light sweep */}
      <motion.div
        animate={{ x: ['-120%', '220%'] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
        className="absolute inset-y-0 w-1/4 pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)' }}
      />
      {/* Inner vignette + sheen border */}
      <div className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{ boxShadow: 'inset 0 0 40px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.06)' }}/>
    </div>
  );
}
